import { NoteSequence } from '@magenta/music/es6/protobuf';
import { sequences } from '@magenta/music/es6/core';
import { MusicVAE } from '@magenta/music/es6/music_vae';
import BaseGenerator, { BaseGeneratorOptions } from './BaseGenerator';
import { ChordProgression } from '../types';
import { flattenChordProgression } from '../magenta-utils';

export type MusicVAEConditioners = {
  /** Degree of similarity between the generated sequences and the input sequence.
   *  Must be between 0 and 1, where 1 is most similar and 0 is least similar. */
  similarity: number;
  /** The softmax temperature to use when sampling. */
  temperature?: number;
  /** Array of chord symbol strings like "Cmaj7", "Abdim", "C#m7". */
  chordProgression?: ChordProgression|string[];
  /** An integer between 0 (C major / A minor) and 11 (B major / G# minor). */
  key?: number;
}

export interface MusicVAEConditioner {
  (context: any, progress: number): MusicVAEConditioners;
}

type MusicVAEGeneratorOptionsExtension = {
  checkpoint: string;
  conditionGenerator?: MusicVAEConditioner;
}

export type MusicVAEGeneratorOptions =
  BaseGeneratorOptions & MusicVAEGeneratorOptionsExtension;

const DEFAULT_CONDITIONERS: MusicVAEConditioners = {
  similarity: 0.75,
  temperature: 1,
  chordProgression: ['Cmaj7', 'Abdim', 'C#m7'],
};

export default class MusicVAEGenerator extends BaseGenerator {
  private model!: MusicVAE;

  private conditionGenerator: MusicVAEConditioner;

  private modelCheckpoint!: string;

  get initialized() {
    return this.model.initialized;
  }

  get checkpoint(): string {
    return this.modelCheckpoint;
  }

  set checkpoint(checkpoint: string) {
    this.modelCheckpoint = checkpoint;
    this.model = new MusicVAE(checkpoint);
  }

  constructor(options: MusicVAEGeneratorOptions) {
    super(options);
    this.conditionGenerator = options.conditionGenerator || (() => DEFAULT_CONDITIONERS);
    this.checkpoint = options.checkpoint;
  }

  async initialize(): Promise<MusicVAEGenerator> {
    if (!this.initialized) {
      await this.model.initialize();
    }
    return this;
  }

  /** Filter out invalid control args for selected checkpoint
   *  Reference: https://github.com/magenta/magenta-js/blob/master/music/src/music_vae/model.ts#L1016
   */
  filterControlArgsForModel(controlArgs: any, warn?: boolean) {
    // TODO: Figure out how to import MusicVAEControlArgs/MusicVAESpec types
    const filteredArgs = { ...controlArgs };
    const warnIgnoredArg = (arg: string) => {
      if (warn) {
        console.warn(`Ignoring invalid argument '${arg}' for selected model.`);
      }
    };
    // Converting to `any` type to access private property `spec`
    const modelSpec = (this.model as any).spec;
    if (!modelSpec.chordEncoder && filteredArgs.chordProgression) {
      warnIgnoredArg('chordProgression');
      delete filteredArgs.chordProgression;
    }
    if (!modelSpec.conditionOnKey && 'key' in filteredArgs) {
      warnIgnoredArg('key');
      delete filteredArgs.key;
    }
    return filteredArgs;
  }

  async generateSequence(seedSequence: NoteSequence, progress: number): Promise<NoteSequence> {
    // Get conditioning parameters
    const {
      similarity, temperature, chordProgression, key,
    } = this.conditionGenerator(this.context, progress);
    const generatedSequences = await this.model.similar(
      seedSequence,
      1,
      similarity,
      temperature,
      this.filterControlArgsForModel({
        chordProgression: chordProgression && (Array.isArray(chordProgression)
          ? chordProgression
          : flattenChordProgression(chordProgression)),
        key,
      }, progress === 0),
    );
    return sequences.concatenate(generatedSequences);
  }
}
